'use client'

import { Button, type ButtonProps } from 'ariakit/button'
import cn from 'clsx'

import css from './IconButton.module.css'

export type IconButtonProps = ButtonProps & {
    icon: React.ElementType /** Which icon is used in the button. */
    size?: '16' | '24' | '30' | '32' | '48' /** The size of the button. */
    radius?:
        | 'var(--radius-sm)'
        | 'var(--radius-md)'
        | '50%' /** The border-radius of the component shape. In px. */
    /** Whether the buttons shows a background by default. */
    /** @deprecated replaced with ghost variant */
    hasBackground?: boolean
    /** Disable the button. */
    disabled?: boolean
    /** @deprecated use aria-label directly */
    ariaLabel?: string
    className?: string
    style?: React.CSSProperties
    onClick?: React.MouseEventHandler<HTMLButtonElement>
    variant?: 'normal' | 'emphasized' | 'ghost' /** The button's visual variant */
}

/** A type of button that just uses icons to indicate their purpose. */
export function IconButton({
    icon,
    size = '24',
    radius = 'var(--radius-md)',
    hasBackground = true,
    disabled = false,
    ariaLabel,
    className,
    style,
    onClick,
    variant = 'normal',
    ...rest
}: IconButtonProps) {
    const Icon = icon

    return (
        <div
            className={cn(
                css.iconButton,
                {
                    [css.emphasized]: variant === 'emphasized',
                    [css.normal]: variant === 'normal',
                    [css.ghost]: variant === 'ghost' || !hasBackground,
                    [css.size16]: size === '16',
                    [css.size24]: size === '24',
                    [css.size32]: size === '32',
                    [css.size30]: size === '30',
                    [css.size48]: size === '48',
                },
                className,
            )}
            style={{ borderRadius: radius, ...style }}
        >
            <Button
                aria-label={ariaLabel}
                title={ariaLabel}
                onClick={(e) => !disabled && onClick && onClick(e)}
                data-size={size}
                disabled={!!disabled}
                className={cn(css.innerContainer, {
                    [css.size16]: size === '16',
                    [css.size24]: size === '24',
                    [css.size32]: size === '32',
                    [css.size30]: size === '30',
                    [css.size48]: size === '48',
                })}
                style={{ borderRadius: radius }}
                {...rest}
            >
                <Icon />
            </Button>
        </div>
    )
}
